import {
  type AsyncStorage,
  experimental_createQueryPersister,
  type PersistedQuery,
} from '@tanstack/query-persist-client-core'
import {
  notifyManager,
  type Query,
  type QueryClient,
  type QueryFunctionContext,
  type QueryKey,
} from '@tanstack/react-query'

import { createClient } from '@/lib/supabase/client'

import { isInboxV2ListKey, listKeyHasSearch } from './inbox-list'

/**
 * Local-first read layer for inbox v2.
 *
 * Every inbox-v2 query family (list, counts, covers, conversation detail,
 * timeline, integrations) opts into a per-query IndexedDB persister so a
 * reload paints instantly from disk instead of cold-fetching. Persistence is
 * READ-path only: restored data is never trusted as complete —
 * `inboxV2QueryPersister` background-revalidates every restored query,
 * and the existing reconcile machinery (realtime patch-first apply, the
 * PROD-5478 conversation-tail guard, reconnect/visibility invalidation)
 * remains the correctness authority. postgres_changes has no replay, so a
 * cache that survived a browser restart has by definition missed every event
 * emitted while the app was closed.
 *
 * Multi-tenant safety: IndexedDB is per-origin, not per-account. The store is
 * bound to the Supabase auth user id; a different user signing in on the same
 * browser profile wipes the store before any read is served. Query keys embed
 * `workspaceId`, so within one account caches never cross workspaces.
 */

const DB_NAME = 'trellis-inbox-v2-cache'
const DB_VERSION = 1
const STORE_NAME = 'queries'
/** Reserved storage key holding the auth user id that owns this cache. */
const OWNER_KEY = '__meta:owner'

export const INBOX_V2_PERSIST_PREFIX = 'trellis-inbox-v2'
/** Bump to invalidate every persisted entry after a breaking shape change. */
export const INBOX_V2_PERSIST_BUSTER = 'inbox-v2-persist-v1'
export const INBOX_V2_PERSIST_MAX_AGE_MS = 7 * 24 * 60 * 60_000
/**
 * Timeline pages are newest-first (page 0 is the live tail). Restoring deep
 * scrollback would make the post-restore revalidate refetch every page, so
 * only the newest pages are persisted.
 */
export const INBOX_V2_PERSIST_MAX_TIMELINE_PAGES = 3
const PERSIST_WRITE_THROTTLE_MS = 1500
/**
 * Conversation lists and their counts are workspace-wide projections that go
 * stale within minutes. A restored snapshot paints the WRONG conversations —
 * rows that arrived since are missing, closed ones linger — for as long as the
 * background revalidate takes (seconds on a busy workspace). Past this age
 * the faithful loading skeleton is shown instead. Detail and timeline
 * snapshots keep restoring: they are per-conversation and tail-guarded.
 */
export const INBOX_V2_PERSIST_PROJECTION_MAX_RESTORE_AGE_MS = 2 * 60_000

function persistDebug(message: string, error: unknown) {
  // Persistence is best-effort by design: a private-mode browser, a blocked
  // IndexedDB, or a storage quota error must degrade to plain network fetches,
  // never to a visible failure. Opening the conversation owns visible errors.
  console.debug(`[inbox-v2] persist: ${message}`, error)
}

let databasePromise: Promise<IDBDatabase | null> | null = null

function openDatabase(): Promise<IDBDatabase | null> {
  if (databasePromise) {
    return databasePromise
  }

  databasePromise = new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME)
        }
      }
      request.onsuccess = () => {
        const database = request.result
        database.onversionchange = () => {
          // Another tab is upgrading or deleting the database — release our
          // connection so it can proceed, and reopen lazily on the next op.
          database.close()
          databasePromise = null
        }
        resolve(database)
      }
      request.onerror = () => {
        persistDebug('IndexedDB open failed', request.error)
        resolve(null)
      }
    } catch (error) {
      persistDebug('IndexedDB unavailable', error)
      resolve(null)
    }
  })
  return databasePromise
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T | undefined> {
  const database = await openDatabase()
  if (!database) {
    return undefined
  }
  try {
    const store = database.transaction(STORE_NAME, mode).objectStore(STORE_NAME)
    return await requestToPromise(run(store))
  } catch (error) {
    persistDebug('IndexedDB operation failed', error)
    return undefined
  }
}

async function resolveSessionUserId(): Promise<string | null> {
  try {
    const { data } = await createClient().auth.getSession()
    return data.session?.user.id ?? null
  } catch (error) {
    persistDebug('auth session lookup failed', error)
    return null
  }
}

let ownerGate: Promise<string | null> | null = null
let authOwnerResetAttached = false
/**
 * Wall-clock of the most recent auth transition. Every write path — including
 * the persister's own `notifyManager`-queued post-fetch writes, which this
 * module cannot cancel — is rejected when the query data predates it, so data
 * produced under the previous account can never land in the next account's
 * rebound store no matter how late the queued write fires.
 */
let lastAuthChangeAtMs = 0
/**
 * Auth generation counter and the generation each query's CURRENT fetch
 * started under. The `dataUpdatedAt` fence alone misses one race: a fetch
 * issued under user A that resolves after the switch to user B carries a
 * fresh post-switch timestamp. Stamping the generation at fetch start and
 * requiring it to still be current at write time closes that hole.
 */
let authEpoch = 0
const fetchEpochByQueryHash = new Map<string, number>()

function stampFetchEpoch(queryHash: string) {
  fetchEpochByQueryHash.set(queryHash, authEpoch)
}

function writeAllowedForEpoch(storageKey: string): boolean {
  const queryHash = storageKey.startsWith(`${INBOX_V2_PERSIST_PREFIX}-`)
    ? storageKey.slice(INBOX_V2_PERSIST_PREFIX.length + 1)
    : storageKey
  return fetchEpochByQueryHash.get(queryHash) === authEpoch
}

/**
 * `ensureOwner` memoizes its verdict, so every auth transition (sign-out,
 * cross-tab account switch via Supabase's auth broadcast) must drop the memo —
 * otherwise this tab keeps serving the PREVIOUS user's persisted entries until
 * a full reload re-runs the owner check. The next storage operation then
 * revalidates against the new session and wipes the store on mismatch.
 */
function attachAuthOwnerReset() {
  if (authOwnerResetAttached) {
    return
  }
  authOwnerResetAttached = true
  try {
    createClient().auth.onAuthStateChange(() => {
      // Scheduled writes close over the pre-switch QueryClient: fired after
      // the store rebinds to the next user, they would copy the previous
      // user's cached rows into the new owner's bucket. Cancel them BEFORE
      // dropping the owner memo; the timestamp fence catches every queued
      // write this module cannot cancel.
      lastAuthChangeAtMs = Date.now()
      authEpoch += 1
      fetchEpochByQueryHash.clear()
      clearPendingPersistTimers()
      ownerGate = null
    })
  } catch (error) {
    persistDebug('auth listener attach failed', error)
  }
}

/**
 * Bind the store to the current auth user and return that user id (or null
 * while no session is resolvable yet). A mismatch — a different account, OR a
 * missing owner sitting on top of orphan query entries (the fingerprint of a
 * write that raced a prior clear) — wipes every persisted entry before any
 * read. The returned id is what write paths re-verify against inside the same
 * IndexedDB transaction as the `put`.
 */
async function ensureOwner(): Promise<string | null> {
  ownerGate ??= (async () => {
    const userId = await resolveSessionUserId()
    if (!userId) {
      ownerGate = null
      return null
    }
    const stored = await withStore<unknown>('readonly', (store) => store.get(OWNER_KEY))
    if (stored === userId) {
      return userId
    }
    if (typeof stored === 'string') {
      await withStore('readwrite', (store) => store.clear())
    } else {
      // No owner recorded. A truly empty store is safe to adopt, but any
      // pre-existing query entry without an owner is a cross-account race
      // artifact (a late write landed after a prior clear) — purge before
      // rebinding so it can never be served to this account.
      const keys = await withStore('readonly', (store) => store.getAllKeys())
      if (Array.isArray(keys) && keys.some((key) => key !== OWNER_KEY)) {
        await withStore('readwrite', (store) => store.clear())
      }
    }
    await withStore('readwrite', (store) => store.put(userId, OWNER_KEY))
    return userId
  })()
  return await ownerGate
}

/**
 * Owner-guarded write, atomic against account switches. The owner read and
 * the `put` share ONE readwrite transaction: IndexedDB serializes it whole,
 * before or after any concurrent clear+rebind, so a value whose owner no
 * longer matches is never written. Closes the race where an authorized write
 * resolves after the store has been cleared and rebound to the next account.
 */
async function putIfOwner(key: string, value: PersistedQuery, ownerId: string): Promise<void> {
  const database = await openDatabase()
  if (!database) {
    return
  }
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const ownerReq = store.get(OWNER_KEY)
      ownerReq.onsuccess = () => {
        if (ownerReq.result === ownerId) {
          store.put(value, key)
        }
        // else: the store was rebound to another account — drop this write.
      }
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('putIfOwner transaction failed'))
      tx.onabort = () => reject(tx.error ?? new Error('putIfOwner transaction aborted'))
    })
  } catch (error) {
    persistDebug('putIfOwner failed', error)
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isPersistedQuery(value: unknown): value is PersistedQuery {
  if (!isRecord(value)) {
    return false
  }
  return (
    typeof value.queryHash === 'string' &&
    typeof value.buster === 'string' &&
    Array.isArray(value.queryKey) &&
    isRecord(value.state)
  )
}

function isInboxV2ProjectionKey(queryKey: QueryKey): boolean {
  const [root, workspaceId] = queryKey
  if (root === 'inbox-v2-counts') {
    return true
  }
  return typeof workspaceId === 'string' && isInboxV2ListKey(queryKey, workspaceId)
}

/** True when a persisted list/counts snapshot is too old to paint as current. */
export function isExpiredInboxV2ProjectionSnapshot(
  persisted: Pick<PersistedQuery, 'queryKey' | 'state'>,
  nowMs: number
): boolean {
  return (
    isInboxV2ProjectionKey(persisted.queryKey) &&
    nowMs - persisted.state.dataUpdatedAt > INBOX_V2_PERSIST_PROJECTION_MAX_RESTORE_AGE_MS
  )
}

const storage: AsyncStorage<PersistedQuery> = {
  getItem: async (key) => {
    if (!(await ensureOwner())) {
      return
    }
    const value = await withStore<unknown>('readonly', (store) => store.get(key))
    if (!isPersistedQuery(value) || isExpiredInboxV2ProjectionSnapshot(value, Date.now())) {
      return
    }
    return value
  },
  setItem: async (key, value) => {
    // Three fences against cross-account leaks: the fetch must have STARTED
    // under the current auth generation, the data must not predate the last
    // auth transition, and the final write is owner-guarded ATOMICALLY (the
    // owner check and the put share one transaction). The first two are
    // re-checked after the async owner gate so a switch landing mid-await
    // still short-circuits; putIfOwner then closes the residual window where
    // a switch lands during the write itself.
    if (!writeAllowedForEpoch(key)) {
      return
    }
    if (value.state.dataUpdatedAt < lastAuthChangeAtMs) {
      return
    }
    const ownerId = await ensureOwner()
    if (!ownerId) {
      return
    }
    if (!writeAllowedForEpoch(key)) {
      return
    }
    if (value.state.dataUpdatedAt < lastAuthChangeAtMs) {
      return
    }
    await putIfOwner(key, value, ownerId)
  },
  removeItem: async (key) => {
    await withStore('readwrite', (store) => store.delete(key))
  },
  entries: async () => {
    if (!(await ensureOwner())) {
      return []
    }
    // Independent reads — issue them together instead of one-after-another.
    const [keys, values] = await Promise.all([
      withStore('readonly', (store) => store.getAllKeys()),
      withStore('readonly', (store) => store.getAll()),
    ])
    if (!(keys && values) || keys.length !== values.length) {
      return []
    }
    const result: [string, PersistedQuery][] = []
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index]
      const value: unknown = values[index]
      if (typeof key !== 'string' || key === OWNER_KEY) {
        continue
      }
      if (!isPersistedQuery(value)) {
        continue
      }
      result.push([key, value])
    }
    return result
  },
}

interface InfinitePagesData {
  pageParams: unknown[]
  pages: unknown[]
}

function isInfinitePagesData(value: unknown): value is InfinitePagesData {
  return isRecord(value) && Array.isArray(value.pages) && Array.isArray(value.pageParams)
}

export function trimPersistedInfinitePages(
  query: PersistedQuery,
  maxPages = INBOX_V2_PERSIST_MAX_TIMELINE_PAGES
): PersistedQuery {
  const data: unknown = query.state.data
  if (!isInfinitePagesData(data) || data.pages.length <= maxPages) {
    return query
  }
  return {
    ...query,
    state: {
      ...query.state,
      data: {
        ...data,
        pages: data.pages.slice(0, maxPages),
        pageParams: data.pageParams.slice(0, maxPages),
      },
    },
  }
}

export function inboxV2PersistEnabled(): boolean {
  if (process.env.NEXT_PUBLIC_INBOX_V2_PERSIST === '0') {
    return false
  }
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined'
}

/**
 * Persister filter — the persister is installed on the QueryClient's
 * `defaultOptions.queries`, so every query passes through it and THIS
 * predicate decides which families actually persist/restore. Everything else
 * falls straight through to its `queryFn`.
 *
 * The param is structurally `{ queryKey }` (wider than TanStack's `Query`) so
 * the predicate stays assignable to `QueryFilters['predicate']` and trivially
 * unit-testable.
 */
export function shouldPersistInboxV2Query(query: { queryKey: QueryKey }): boolean {
  const { queryKey: key } = query
  const [root, workspaceId] = key
  if (typeof root !== 'string') {
    return false
  }

  if (root === 'inbox-v2') {
    if (typeof workspaceId !== 'string') {
      return false
    }
    // Search pages are transient and keyed by free text — never persist them.
    if (isInboxV2ListKey(key, workspaceId)) {
      return !listKeyHasSearch(key)
    }
    return key[1] === 'conversation'
  }
  if (root === 'timeline-v2') {
    // Deep-link windows (`around=` first pages, key[3] = target event id) are
    // positional one-offs; restoring one would render a mid-history window as
    // the chat tail. Only the live tail (target null) persists.
    return key.length >= 4 && key[3] === null
  }
  return (
    root === 'inbox-v2-counts' ||
    root === 'inbox-v2-property-covers' ||
    root === 'v2-conversation-integrations'
  )
}

type InboxV2Persister = ReturnType<typeof experimental_createQueryPersister<PersistedQuery>>

let persister: InboxV2Persister | null = null

function getPersister(): InboxV2Persister | null {
  if (!inboxV2PersistEnabled()) {
    return null
  }
  attachAuthOwnerReset()
  persister ??= experimental_createQueryPersister<PersistedQuery>({
    storage,
    buster: INBOX_V2_PERSIST_BUSTER,
    maxAge: INBOX_V2_PERSIST_MAX_AGE_MS,
    prefix: INBOX_V2_PERSIST_PREFIX,
    // The wrapper below owns background revalidation. The experimental
    // persister drops query.fetch()'s promise, so logout during a restored
    // query's refresh would produce an unhandled CancelledError.
    refetchOnRestore: false,
    // IndexedDB stores structured clones — skip JSON round-trips entirely.
    serialize: trimPersistedInfinitePages,
    deserialize: (value) => value,
    filters: { predicate: shouldPersistInboxV2Query },
  })
  return persister
}

/**
 * `persister` option for inbox-v2 query/infinite-query options. Undefined on
 * the server, when IndexedDB is unavailable, or when the
 * `NEXT_PUBLIC_INBOX_V2_PERSIST=0` kill switch is set. Wraps the underlying
 * persister so every fetch stamps the auth generation it started under —
 * the write fence then rejects responses that resolve after a switch.
 */
export function inboxV2QueryPersister() {
  const active = getPersister()
  if (!active) {
    return
  }
  const delegate = active.persisterFn
  return async function inboxV2PersisterFn<T, TQueryKey extends QueryKey>(
    queryFn: (context: QueryFunctionContext<TQueryKey>) => T | Promise<T>,
    context: QueryFunctionContext<TQueryKey>,
    query: Query
  ): Promise<T> {
    stampFetchEpoch(query.queryHash)
    const fetchEpoch = authEpoch
    let fetched = false
    const value = await delegate(
      (ctx) => {
        fetched = true
        return queryFn(ctx)
      },
      context,
      query
    )
    if (!fetched) {
      // Let Query publish the restored data and timestamp before refreshing,
      // even when staleTime marks it fresh. Do not restart a removed query
      // or a fetch from the previous account after logout/cache clearing.
      notifyManager.schedule(() => {
        if (
          fetchEpoch === authEpoch &&
          context.client.getQueryCache().get(query.queryHash) === query
        ) {
          // Query keeps network failures in its error state for observers.
          // This detached refresh must also handle its rejected promise.
          query.fetch().catch(() => undefined)
        }
      })
    }
    return value
  }
}

/**
 * Persist the CURRENT cached state of a query. The persister only writes on
 * `queryFn` completion, so `setQueryData` writes (prefetch seeding, realtime
 * patches) must persist explicitly or the disk copy silently falls behind.
 */
export async function persistInboxV2QueryByKey(queryClient: QueryClient, queryKey: QueryKey) {
  const active = getPersister()
  if (!active) {
    return
  }
  try {
    const query = queryClient.getQueryCache().find({ queryKey })
    if (!query) {
      return
    }
    // Explicit writes persist the CURRENT cached state under the CURRENT
    // auth generation; a switch landing mid-await is caught by the fence.
    stampFetchEpoch(query.queryHash)
    await active.persistQueryByKey(queryKey, queryClient)
  } catch (error) {
    persistDebug('persistQueryByKey failed', error)
  }
}

const pendingPersistTimers = new Map<string, ReturnType<typeof setTimeout>>()

function clearPendingPersistTimers() {
  for (const timer of pendingPersistTimers.values()) {
    clearTimeout(timer)
  }
  pendingPersistTimers.clear()
}

/**
 * Trailing-edge throttle around {@link persistInboxV2QueryByKey} so realtime
 * event bursts collapse into one IndexedDB write per query key.
 */
export function schedulePersistInboxV2Query(
  queryClient: QueryClient,
  queryKey: QueryKey,
  delayMs = PERSIST_WRITE_THROTTLE_MS
) {
  if (!inboxV2PersistEnabled()) {
    return
  }
  const timerKey = JSON.stringify(queryKey)
  if (pendingPersistTimers.has(timerKey)) {
    return
  }
  pendingPersistTimers.set(
    timerKey,
    setTimeout(() => {
      pendingPersistTimers.delete(timerKey)
      persistInboxV2QueryByKey(queryClient, queryKey)
    }, delayMs)
  )
}

/**
 * Flush EVERY manual `setQueryData` write to a persisted inbox-v2 family to
 * disk. The persister only writes on `queryFn` completion; realtime patches,
 * optimistic status/draft updates, and prefetch seeding all mutate the cache
 * via `setQueryData`, which is invisible to it. Without this a reload could
 * restore a pre-patch snapshot (e.g. a conversation still TODO after realtime
 * moved it to DONE) until the background revalidate lands. One cache
 * subscription covers all of them; the per-key throttle collapses bursts.
 *
 * Returns an unsubscribe. No-op (returns a noop) when persistence is off.
 */
export function installInboxV2PersistSubscription(queryClient: QueryClient): () => void {
  if (!inboxV2PersistEnabled()) {
    return () => undefined
  }
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') {
      return
    }
    const { action } = event
    // Only MANUAL success updates (setQueryData); fetch completions already
    // go through the persister's own write path.
    if (action.type !== 'success' || action.manual !== true) {
      return
    }
    if (!shouldPersistInboxV2Query(event.query)) {
      return
    }
    schedulePersistInboxV2Query(queryClient, event.query.queryKey)
  })
}

/** Drop expired/busted/malformed entries. Cheap; run once per session. */
export async function inboxV2PersisterGc() {
  const active = getPersister()
  if (!active) {
    return
  }
  try {
    await active.persisterGc()
  } catch (error) {
    persistDebug('gc failed', error)
  }
}

function keyReferencesWorkspace(queryKey: QueryKey, workspaceId: string): boolean {
  // Inbox families carry the workspace id at index 1, except the
  // conversation-detail family (['inbox-v2', 'conversation', ws, …]) at 2.
  return queryKey[1] === workspaceId || queryKey[2] === workspaceId
}

/**
 * Access-refusal hook for WORKSPACE-scoped inbox fetchers (list, counts,
 * covers): a 403/404 from those routes means the workspace itself is no
 * longer accessible, so its at-rest and in-memory copies are dropped.
 * Fire-and-forget by design — the caller still throws its fetch error.
 */
export function purgeInboxV2PersistedCacheOnAccessError(
  workspaceId: string,
  status: number,
  queryClient?: QueryClient
) {
  if (status !== 403 && status !== 404) {
    return
  }
  // Restored data may already be rendered when the refusal arrives — drop the
  // in-memory copies too, not just the disk ones. Deferred a tick because
  // this fires from inside the failing queryFn and must not remove the query
  // out from under its own in-flight fetch.
  if (queryClient) {
    setTimeout(() => {
      queryClient.removeQueries({
        predicate: (query) =>
          shouldPersistInboxV2Query(query) && keyReferencesWorkspace(query.queryKey, workspaceId),
      })
    }, 0)
  }
  purgeInboxV2WorkspacePersistedCache(workspaceId)
}

/**
 * Variant for CONVERSATION-scoped fetchers (detail, timeline, integrations):
 * those routes legitimately 404 for a missing/deleted conversation, which is
 * NOT a workspace access loss — wiping the whole workspace cache on a stale
 * chat link would defeat the local-first cache. Only an explicit 403 purges.
 */
export function purgeInboxV2PersistedCacheOnForbidden(
  workspaceId: string,
  status: number,
  queryClient?: QueryClient
) {
  if (status !== 403) {
    return
  }
  purgeInboxV2PersistedCacheOnAccessError(workspaceId, status, queryClient)
}

/**
 * Drop every persisted entry of one workspace. Fired when the server refuses
 * inbox access (membership revoked mid-session): render paths are already
 * gated server-side, but the at-rest copy must not outlive the refusal.
 */
export async function purgeInboxV2WorkspacePersistedCache(workspaceId: string) {
  const active = getPersister()
  if (!(active && workspaceId)) {
    return
  }
  try {
    // Independent — read the entries and open the database together.
    const [stored, database] = await Promise.all([storage.entries?.(), openDatabase()])
    if (!(stored && database)) {
      return
    }
    const keysToDelete: string[] = []
    for (const [key, value] of stored) {
      if (keyReferencesWorkspace(value.queryKey, workspaceId)) {
        keysToDelete.push(key)
      }
    }
    if (keysToDelete.length === 0) {
      return
    }
    // Delete every matching key in ONE transaction instead of awaiting one
    // round-trip per key.
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      for (const key of keysToDelete) {
        store.delete(key)
      }
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('workspace purge transaction failed'))
      tx.onabort = () => reject(tx.error ?? new Error('workspace purge aborted'))
    })
  } catch (error) {
    persistDebug('workspace purge failed', error)
  }
}

/** Wipe the persisted cache (sign-out, account switch). */
export async function clearInboxV2PersistedCache() {
  clearPendingPersistTimers()
  ownerGate = null
  await withStore('readwrite', (store) => store.clear())
}
